import React, { cloneElement, useContext } from 'react';
import {
    Edit as RAEdit,
    Create as RACreate,
    List as RAList,
    useGetOne,
    SimpleForm,
    TabbedForm,
    TopToolbar,
    sanitizeListRestProps,
    Button,
    CreateButton,
    BulkDeleteButton,
    ExportButton,
    EditButton,
} from 'react-admin';
import {
    ACTION_CREATE,
    ACTION_EDIT,
    ACTION_LIST,
    ACTION_VIEW,
    useResourceActionPermission
} from 'utils/permissions';
import { Box, makeStyles, Typography } from "@material-ui/core";
import { validationErrorsToDisplayText } from "../utils/validationParser";
import { HostContext } from "../App";
import pDebounce from "p-debounce";
import request from "../utils/request";
import ClearAllIcon from "@material-ui/icons/ClearAll";

export function AppEdit({titleProp = 'name', permission, children, ...props}) {
    const {data} = useGetOne(props.resource, props.id);
    const title = data && data[titleProp];

    const hasEditPermission = useResourceActionPermission(permission ? permission : props.resource, ACTION_EDIT)?.hasPermission;
    const hasViewPermission = useResourceActionPermission(permission ? permission : props.resource, ACTION_VIEW)?.hasPermission;

    if(!(hasEditPermission || hasViewPermission)) {
        return <PermissionDenied />;
    }

    return (
        <RAEdit title={`${props?.options?.label} ${title}`} {...props} redirect='list'>
            {children}
        </RAEdit>
    );
}

export function AppCreate({ children, permission = undefined, ...props}) {
    const hasCreatePermission = useResourceActionPermission(permission ? permission : props.resource, ACTION_CREATE)?.hasPermission;

    if(!hasCreatePermission) {
        return <PermissionDenied />;
    }

    return (
        <RACreate title={props?.options?.label} {...props} redirect='list'>
         {children}
        </RACreate>
    );
}
const BulkActionButtons = props => (
    <>
        {/* default bulk delete action */}
        <BulkDeleteButton {...props} />
    </>
);

const AppListActionsTopToolbar = (props) => {
    const { resource, showFilter, displayedFilters, filterValues, setFilters, children } = props;
    const { className, permission = undefined, ...rest } = props;
    const { hasPermission: hasCreatePermission } = useResourceActionPermission(permission ? permission : resource, ACTION_CREATE);

    const showCreate = (hasCreatePermission);
    const showClearAllFilters = (Object.getOwnPropertyNames(filterValues).length > 0);

    return (
      <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
          {children}
          {/* Clear Filters Button */}
          { showClearAllFilters &&
              <Button onClick={() => setFilters({})} label="Clear Filters" >
                  <ClearAllIcon />
              </Button>
          }
          {  cloneElement(props.filters, {
              resource,
              showFilter,
              displayedFilters,
              filterValues,
              context: 'button',
            })
          }
          { showCreate && <CreateButton/> }
          <ExportButton />
      </TopToolbar>
    );
}

export function AppList({children, permission = undefined, extraActions, ...props}) {
    const { hasPermission: hasListPermission } = useResourceActionPermission(permission ? permission : props.resource, ACTION_LIST);
    const { hasPermission: hasCreatePermission } = useResourceActionPermission(permission ? permission : props.resource, ACTION_CREATE);

    if(!hasListPermission) {
        return <PermissionDenied />
    }

    return (
        <RAList
        //   bulkActionButtons={true}
          bulkActionButtons={<BulkActionButtons />}
          actions={<AppListActionsTopToolbar>{extraActions}</AppListActionsTopToolbar>}
          {...props}
          hasCreate={!!hasCreatePermission}
        >
            {children}
        </RAList>
    );
}

// this allows us to join with \n below and have line breaks.
const useStyles = makeStyles({
    error: {
        '& .Mui-error': {
            whiteSpace: 'pre'
        }
    }
})

export const PermissionDenied = ()  => (
  <Box textAlign="center" paddingTop={10}>
      <Typography variant="h5" paragraph>
          Permission Denied
      </Typography>
      <Typography variant="body1" color="textSecondary">
          You are not permitted to perform this action.
      </Typography>
  </Box>
)

function AppForm(Component, { children, formAction = 'create',  ...props }) {
    const {resource} = props;

    let mutateType = formAction;

    if (formAction === 'edit') {
        mutateType = 'update';
    }

    const styles = useStyles();

    const { apiHost, apiToken } = useContext(HostContext);

    const validate = async (values) => {
        const url = '/api/validation/check-data';
        const postData = {
            'action': `${resource}-${mutateType}`,
            data: { ...values }
        };
        const config = { headers: { Authorization: `Bearer ${apiToken}` } };

        const { data } = await request(apiHost).post(url, postData, config);

        if (!data?.errors) {
            return {};
        }

        return validationErrorsToDisplayText(data?.errors);
    };

    // async debounce for validation
    // reduces server load by waiting 500ms after last change
    const validateDebounced = pDebounce(validate, 500);

    return (
      <Component {...props} validate={validateDebounced} className={styles.error}>
          {children}
      </Component>
    );
}

export function AppSimpleForm({ formAction = 'create', ...props}) {
    return AppForm(SimpleForm, { formAction, ...props });
}

export function AppTabbedForm({ formAction = 'create', ...props}) {
    return AppForm(TabbedForm, { formAction, ...props });
}


export function EditAction(props) { 
    const {resource, record} = props;
    // const [update, { loading, error }] = useUpdate();
    // const handleClick = () => {
    //     const updatedData = {...record, deleted: false};
    //     console.log('updatedData', updatedData )
    //     update(resource, record?._id, updatedData, record);
    // }

    if (record?.deleted) {
        return null;
        // if(loading) {
        //     return <Loading />
        // }
    //   return <Button color='primary' onClick={handleClick}><>Restore</></Button>
    }
  
    return <EditButton {...props} />
  }